/**
 * SPDX-FileCopyrightText: Ferdinand Thiessen <opensource@fthiessen.de>
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import type { IRichObject } from '../types'
import type { Component, Ref } from 'vue'

import { computed } from 'vue'

import NcUserBubble from '@nextcloud/vue/dist/Components/NcUserBubble.js'
import UnknownArgument from '../components/RichArguments/UnknownArgument.vue'

export interface IRichArgument {
	component: Component
	props: Record<string, unknown>
}

/**
 * Map an collection of rich text objects to rich arguments for the RichText component
 *
 * @param richObjects The rich text object
 */
export function mapRichObjectsToRichArguments(
	richObjects: Record<string, IRichObject>,
) {
	const args: Record<string, IRichArgument> = {}

	for (const richObjectName in richObjects) {
		args[richObjectName] = mapRichObjectToRichArgument(
			richObjects[richObjectName],
		)
	}

	return args
}

/**
 * Map rich text object to rich argument for the RichText component
 *
 * @param {Record<string, IRichObject>} richObject - The rich text object
 * @return {object}}
 */
export function mapRichObjectToRichArgument(richObject: IRichObject) {
	switch (richObject.type) {
		case 'user':
			return {
				component: NcUserBubble,
				props: {
					displayName: richObject.name,
					user: richObject.id,
					url: richObject.link,
				},
			}
		case 'group':
			return {
				component: NcUserBubble,
				props: {
					avatarImage: 'icon-group',
					displayName: richObject.name,
					url: richObject.link,
					primary: true,
				},
			}
		default:
			return {
				component: UnknownArgument,
				props: richObject,
			}
	}
}

/**
 * Reactivly map rich objects to rich arguments for use with NcRichText
 * @param objects Map of RichObjects
 */
export function useRichArguments(objects: Ref<Record<string, IRichObject>>) {
	return {
		richArguments: computed(() => mapRichObjectsToRichArguments(objects.value)),
	}
}
